import React, { useCallback, useContext, useEffect, useState } from "react";
import ReactPaginate from "react-paginate";
import styled from "styled-components";

import ActiveFilters from "components/collection/ActiveFilters";
import FilterModal from "components/collection/FilterModal";
import SortModal from "components/collection/SortModal";
import FilterAccordion from "components/collection/FilterAccordion";
import SortDropdown from "components/collection/SortDropdown";
import ProductCard from "components/product/Card";
import StoreContext from "context/Store";

import { Container, Row, Col } from "components/Grid";

import { Paragraph, LinkDecorated } from "components/Typography";

const Main = styled.div`
  position: relative;
  margin: 0 0 0 0;
`;

const GridHeader = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin: 16px 0 6px 0;
`;

const StyledLinkDecorated = styled(LinkDecorated)`
  margin-bottom: 12px;
  font-size: 14px;
`;

const ModalToggles = styled.div`
  position: relative;
  display: none;
  height: 40px;
  border-top: solid 1px ${(p) => p.theme.colors.ltGreen};
  border-bottom: solid 1px ${(p) => p.theme.colors.ltGreen};

  ${(p) => p.theme.media.maxWidth("tablet")} {
    display: flex;
  }
`;

const Modals = styled.div`
  ${(p) => p.theme.media.minWidth("tablet")} {
    display: none;
  }
`;

const ModalToggle = styled.button`
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
  font-size: 14px;
  border-left: solid 1px ${(p) => p.theme.colors.ltGreen};
`;

const StyledSortDropdown = styled(SortDropdown)`
  display: none;

  ${(p) => p.theme.media.minWidth("tablet")} {
    display: block;
  }
`;

const StyledFilterAccordion = styled(FilterAccordion)`
  display: none;

  ${(p) => p.theme.media.minWidth("tablet")} {
    display: block;
  }
`;

const PaginationContainer = styled.div`
  ul {
    display: flex;
    align-items: center;
    justify-content: center;
    li {
      list-style: none;
      margin: 0 5px;
      &.disabled {
        display: none;
      }
      &.selected {
        a {
          background-color: ${(p) => p.theme.colors.ltGreen};
        }
      }
      &.break {
        a {
          pointer-events: none;
          cursor: default;
          background-color: transparent;
          padding: 7px 0;
        }
      }
      a {
        display: block;
        min-width: 30px;
        background-color: ${(p) => p.theme.colors.dkGreen};
        text-align: center;
        padding: 7px 10px;
      }
    }
  }
  ${(p) => p.theme.media.minWidth("tablet")} {
    ul {
      li {
        a {
          padding: 7px 21px;
        }
      }
    }
  }
`;

const pageSize = 18;

const CollectionGrid = (props) => {
  const {
    collection: { filters, setFilterModalActive, setSortModalActive, sort },
  } = useContext(StoreContext);

  const handle = props?.handle?.split("-")?.[0] ?? "mens";
  const totalProducts = props.products
    .filter((product) => {
      return (
        filters.length === 0 ||
        (() => {
          let visible;

          filters.forEach((filter) => {
            const [domain, name, value] = filter.split(":");
            switch (domain) {
              case "variant":
                product.options.forEach((option) => {
                  const isTypedSizeMatch =
                    option.name === "Size" &&
                    name === `${product.productType} ${option.name}`;

                  if (
                    (isTypedSizeMatch || name === option.name) &&
                    option.values.includes(value)
                  ) {
                    visible = true;
                  }
                });
                break;
              case "collection":
                product.collections.forEach((collection) => {
                  if (collection.handle === name) visible = true;
                });
                break;
              default:
                visible = false;
            }
          });

          return visible;
        })()
      );
    })
    .sort((a, b) => {
      switch (sort) {
        case "latest":
          return true;
        case "trending":
          return true;
        case "price-lh":
          return parseFloat(a.variants[0].price) <
            parseFloat(b.variants[0].price)
            ? -1
            : 1;
        case "price-hl":
          return parseFloat(a.variants[0].price) <
            parseFloat(b.variants[0].price)
            ? 1
            : -1;
        default:
          return true;
      }
    });

  const displayProducts = totalProducts;

  const renderProducts = (displayProducts) => {
    if (displayProducts.length > 0) {
      return displayProducts.map((product, i) => (
        <Col mb={12} tblg={8} key={i}>
          <ProductCard {...product} />
        </Col>
      ));
    } else {
      return (
        <Col>
          <Paragraph>No products matched your search criteria.</Paragraph>
        </Col>
      );
    }
  };

  return (
    <>
      <Main>
        <ModalToggles>
          <ModalToggle onClick={() => setFilterModalActive(true)}>
            Filter
          </ModalToggle>
          <ModalToggle onClick={() => setSortModalActive(true)}>
            Sort
          </ModalToggle>
        </ModalToggles>
        <Container>
          <Row>
            <Col tb={17} tbPush={7} tblg={18} tblgPush={6} lg={19} lgPush={5}>
              <GridHeader>
                <ActiveFilters {...props} />
                <StyledSortDropdown />
              </GridHeader>
            </Col>
          </Row>
          <Row>
            <Col tb={7} tblg={6} lg={4}>
              <StyledLinkDecorated to={`/collections/${handle}`}>
                Shop All
              </StyledLinkDecorated>
              <StyledFilterAccordion {...props} />
            </Col>
            <Col tb={17} tblg={18} lg={19} lgPush={1}>
              <PaginatedItems
                itemsPerPage={pageSize}
                displayProducts={displayProducts}
                renderProducts={renderProducts}
              />
            </Col>
          </Row>
        </Container>
      </Main>

      <Modals>
        <FilterModal {...props} />
        <SortModal />
      </Modals>
    </>
  );
};

export default CollectionGrid;

const PaginatedItems = ({ itemsPerPage, displayProducts, renderProducts }) => {
  const [pageNumber, setPageNumber] = useState(1);
  const isBrowser = typeof window !== "undefined";

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const pageParam = parseInt(urlParams.get("page")) || 1;

    if (pageParam === 1) {
      const url = new URL(window.location);
      url.searchParams.set("page", pageParam);
      window.history.pushState(null, "", url.toString());
    }

    setPageNumber(pageParam);
  }, []);

  const handlePageClick = useCallback(
    (selectedPage) => {
      const newPageNumber = selectedPage.selected + 1;
      setPageNumber(newPageNumber);
      if (isBrowser) {
        const url = new URL(window.location);
        url.searchParams.set("page", newPageNumber);
        window.history.pushState(null, "", url.toString());
        window.scrollTo({ top: 0, left: 0 });
      }
    },
    [isBrowser]
  );

  const itemOffset = (pageNumber - 1) * itemsPerPage;
  const currentItems = displayProducts.slice(
    itemOffset,
    itemOffset + itemsPerPage
  );
  const pageCount = Math.ceil(displayProducts.length / itemsPerPage);

  return (
    <PaginationContainer>
      <Row>{renderProducts(currentItems)}</Row>
      <ReactPaginate
        key={pageNumber}
        breakLabel="..."
        disableInitialCallback
        nextLabel=">"
        initialPage={pageNumber - 1}
        onPageChange={handlePageClick}
        pageRangeDisplayed={2}
        marginPagesDisplayed={2}
        pageCount={pageCount}
        previousLabel="<"
        renderOnZeroPageCount={null}
      />
    </PaginationContainer>
  );
};